import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Dealers() {
  const [dealers, setDealers] = useState([]);
  const [users, setUsers] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [openFor, setOpenFor] = useState(null); // dealer object whose addresses popup is open

  const load = () => {
    api.get("/dealers").then(({ data }) => setDealers(data));
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const patchDealer = async (dealer_id, updates) => {
    setSavingId(dealer_id);
    try {
      const { data } = await api.patch(`/dealers/${dealer_id}`, updates);
      setDealers((prev) => prev.map((d) => (d.dealer_id === dealer_id ? data : d)));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingId(null);
    }
  };

  const patchAddress = async (dealer_id, address_id, updates) => {
    try {
      const { data } = await api.patch(`/dealers/${dealer_id}/addresses/${address_id}`, updates);
      setDealers((prev) => prev.map((d) => (d.dealer_id === dealer_id ? { ...d, addresses: data.addresses } : d)));
      setOpenFor((prev) => (prev && prev.dealer_id === dealer_id ? { ...prev, addresses: data.addresses } : prev));
      toast.success("Location saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save location");
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Dealers</h1>
        <Badge variant="outline">{dealers.length} dealers</Badge>
      </div>

      <p className="text-sm text-muted-foreground">
        Each dealer can have multiple locations. Area, Sales Officer, and Manager are set per
        location — click "Addresses" to view and edit them.
      </p>

      <Card className="divide-y overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Credit Limit</th>
                <th className="px-4 py-3 font-medium">Current Outstanding</th>
                <th className="px-4 py-3 font-medium">Addresses</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dealers.map((d) => (
                <tr key={d.dealer_id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 text-sm font-mono whitespace-nowrap">{d.code}</td>
                  <td className="px-4 py-3 text-sm font-medium">{d.name}</td>
                  <td className="px-4 py-3">
                    <Input
                      type="number"
                      className="h-8 w-32"
                      defaultValue={d.credit_limit ?? 0}
                      disabled={savingId === d.dealer_id}
                      onBlur={(e) => {
                        const val = Number(e.target.value) || 0;
                        if (val !== d.credit_limit) patchDealer(d.dealer_id, { credit_limit: val });
                      }}
                    />
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground italic whitespace-nowrap">
                    — <span className="not-italic">(SAP sync — later)</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setOpenFor(d)}>
                      <MapPin className="h-3.5 w-3.5" />
                      {(d.addresses || []).length} location{(d.addresses || []).length === 1 ? "" : "s"}
                    </Button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Badge variant={d.active === false ? "destructive" : "outline"}>
                      {d.active === false ? "Inactive" : "Active"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        {dealers.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No dealers yet — import them from SAP Sync, or add one manually.
          </div>
        )}
        </div>
      </Card>

      <Dialog open={!!openFor} onOpenChange={(o) => { if (!o) setOpenFor(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{openFor?.name} — locations</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {(openFor?.addresses || []).map((addr) => (
              <AddressCard
                key={addr.id}
                addr={addr}
                users={users}
                onSave={(updates) => patchAddress(openFor.dealer_id, addr.id, updates)}
              />
            ))}
            {(openFor?.addresses || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No locations on file for this dealer yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddressCard({ addr, onSave, users = [] }) {
  const [area, setArea] = useState(addr.area || "");
  const [salesOfficer, setSalesOfficer] = useState(addr.sales_officer || "");
  const [manager, setManager] = useState(addr.manager || "");
  const [dirty, setDirty] = useState(false);

  // Filter user options by role
  const salesOfficers = users.filter(u => u.role === "sales" || u.role === "admin");
  const managers = users.filter(u => u.role === "manager" || u.role === "admin");
  const areas = [...new Set(users.map(u => u.area).filter(Boolean)), "South Chennai", "North Chennai", "Coimbatore", "Madurai", "Trichy", "Salem"];

  const save = () => {
    onSave({ area, sales_officer: salesOfficer, manager });
    setDirty(false);
  };

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium">{addr.label}</span>
          <Badge variant="outline" className="ml-2 text-xs">{addr.kind}</Badge>
          {addr.is_default && <Badge className="ml-1 text-xs">Default</Badge>}
        </div>
        <span className="text-xs text-muted-foreground font-mono">{addr.id}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}, {addr.city} {addr.pincode}
        {addr.gstin ? ` · GSTIN ${addr.gstin}` : ""}
      </div>

      {addr.kind !== "billing" && (
        <div className="grid grid-cols-3 gap-3 pt-1">
          <div>
            <Label className="text-xs font-medium">Area</Label>
            <select
              className="h-9 w-full mt-1 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              value={area}
              onChange={(e) => { setArea(e.target.value); setDirty(true); }}
            >
              <option value="">Select Area...</option>
              {areas.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div>
            <Label className="text-xs font-medium">Sales Officer</Label>
            <select
              className="h-9 w-full mt-1 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              value={salesOfficer}
              onChange={(e) => { setSalesOfficer(e.target.value); setDirty(true); }}
            >
              <option value="">Select Sales Officer...</option>
              {salesOfficers.map(so => <option key={so.user_id || so.email} value={so.name}>{so.name} ({so.email})</option>)}
            </select>
          </div>

          <div>
            <Label className="text-xs font-medium">Manager</Label>
            <select
              className="h-9 w-full mt-1 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              value={manager}
              onChange={(e) => { setManager(e.target.value); setDirty(true); }}
            >
              <option value="">Select Manager...</option>
              {managers.map(m => <option key={m.user_id || m.email} value={m.name}>{m.name} ({m.manager_tier || 'Manager'})</option>)}
            </select>
          </div>
        </div>
      )}

      {dirty && (
        <div className="flex justify-end pt-2">
          <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC]" onClick={save}>Save location</Button>
        </div>
      )}
    </Card>
  );
}
